import { format, isValid, parseISO } from 'date-fns';
import { useMemo } from 'react';
import AppConfig from '~/utils/appconfig';

/**
 * A custom hook that provides information about the terms of use document.
 *
 * @returns An object containing:
 * - `termsOfUseLastUpdatedDate`: The parsed `Date` object representing the last updated date
 *    of the terms of use document, or `undefined` if the document is not defined.
 * - `lastUpdatedPrettyPrint`: A formatted string representation of the `lastUpdated` date.
 * - `url`: The URL of the terms of use document, or `undefined` if not specified.
 * - `label`: A human-readable label for the terms of use document, defaulting to "Terms of Use" if not specified.
 * - `isTermsOfUseDocumentAvailable`: A boolean indicating whether the terms of use document is available (i.e.,
 *    defined, has a valid URL, and a valid last updated date).
 *
 * @throws Will throw an error if:
 * - The `lastUpdated` date of the terms of use document is invalid.
 * - The URL of the terms of use document is not specified.
 */
export function useTermsOfUseDocument() {
    const termsOfUseDoc = AppConfig.licenseAndServiceTermsDocUrl;

    const lastUpdated = useMemo(() => {
        // if terms of use doc wasn't defined in the config, return undefined
        if (!termsOfUseDoc) {
            return undefined;
        }

        // if the doc is defined but looks wrong, throw
        const parsedDate = parseISO(termsOfUseDoc?.lastUpdated || '');
        if (!isValid(parsedDate)) {
            throw new Error(
                `Terms of use document lastUpdated date is invalid: ${termsOfUseDoc.lastUpdated}`
            );
        }

        // if no URL specified, throw
        if (!termsOfUseDoc?.url) {
            throw new Error('Terms of use document URL is not specified');
        }

        return parsedDate;
    }, [termsOfUseDoc]);

    const url = termsOfUseDoc?.url;
    const label = termsOfUseDoc?.label || 'Terms of Use';

    const lastUpdatedPrettyPrint = useMemo(() => {
        if (lastUpdated && isValid(lastUpdated)) {
            return format(lastUpdated, 'PP');
        }
        return undefined;
    }, [lastUpdated]);

    const isTermsOfUseDocumentAvailable = Boolean(
        termsOfUseDoc && url && lastUpdated
    );

    return {
        lastUpdated,
        lastUpdatedPrettyPrint,
        url,
        label,
        isTermsOfUseDocumentAvailable,
    };
}
